import React from 'react';
import ReactDOM from 'react-dom/client';
import { QueryClient, QueryClientProvider, QueryCache } from '@tanstack/react-query';
import { BrowserRouter } from 'react-router-dom';
import { ThemeProvider } from 'next-themes';
import { toast } from 'sonner';
import { Toaster } from '@/components/ui/sonner';
import { VersionWatcher } from '@/components/system/VersionWatcher';
import AppErrorBoundary from '@/components/system/AppErrorBoundary';
import { installChunkErrorReload, stripReloadParam } from '@/lib/chunk-error';
import { Analytics } from '@vercel/analytics/react';
import App from './App';
import { AuthProvider } from '@/hooks/useAuth';
// Fuentes autoalojadas (sin llamadas a Google Fonts) — mismos nombres de familia
import '@fontsource/outfit/300.css';
import '@fontsource/outfit/400.css';
import '@fontsource/outfit/500.css';
import '@fontsource/outfit/600.css';
import '@fontsource/outfit/700.css';
import '@fontsource/space-grotesk/300.css';
import '@fontsource/space-grotesk/400.css';
import '@fontsource/space-grotesk/500.css';
import '@fontsource/space-grotesk/600.css';
import '@fontsource/space-grotesk/700.css';
import '@fontsource/jetbrains-mono/400.css';
import '@fontsource/jetbrains-mono/500.css';
import '@fontsource/jetbrains-mono/600.css';
import './index.css';
import { installDevelopmentEnvironment } from '@/lib/development-environment';

// Recuperación global ante chunks viejos tras un deploy (preloadError / rechazo
// no capturado), además del ErrorBoundary que cubre el caso de render.
installDevelopmentEnvironment();
installChunkErrorReload();
// Quita el `_r=` que añade la recarga forzada (cosmético, no rompe el router).
stripReloadParam();

const queryClient = new QueryClient({
  // Red de seguridad app-wide: ninguna carga falla en silencio. Antes, 0 páginas
  // manejaban isError → una query caída dejaba la pantalla girando o con un vacío
  // indistinguible de "no hay datos". Avisamos por toast, deduplicado (un solo id)
  // para no hacer spam si fallan varias a la vez, y solo cuando NO hay datos en
  // caché (un refetch fallido en segundo plano no debe asustar).
  queryCache: new QueryCache({
    onError: (_error, query) => {
      if (query.state.data === undefined) {
        toast.error('No se pudieron cargar algunos datos', {
          id: 'query-load-error',
          description: 'Revisa tu conexión; se reintenta al volver a la pestaña.',
        });
      }
    },
  }),
  defaultOptions: {
    queries: {
      retry: 1,
      // Datos considerados frescos sólo 30s para que el Dashboard refleje
      // cambios hechos en otras pestañas casi en tiempo real.
      staleTime: 30 * 1000,
      // Al volver a la pestaña/ventana, refrescar.
      refetchOnWindowFocus: true,
      // Al montar el componente, refrescar siempre.
      refetchOnMount: 'always',
    },
  },
});

ReactDOM.createRoot(document.getElementById('root')!).render(
  <React.StrictMode>
    <QueryClientProvider client={queryClient}>
      <ThemeProvider attribute="class" defaultTheme="dark" enableSystem={false} disableTransitionOnChange>
        <BrowserRouter>
          <AppErrorBoundary>
            <AuthProvider>
              <App />
            </AuthProvider>
          </AppErrorBoundary>
        </BrowserRouter>
        <VersionWatcher />
        <Toaster />
        <Analytics />{/* Analítica sin cookies (Vercel Web Analytics) */}
      </ThemeProvider>
    </QueryClientProvider>
  </React.StrictMode>
);
